/**
 * Declarative schemas for the schema-driven Admin Config panels.
 *
 * Each entry describes a single live-config document — its location
 * (Firestore vs Realtime Database), human-friendly title/description, the
 * sub-group it belongs to, and the typed fields the admin UI should render.
 * `SchemaConfigPanel` consumes one `ConfigSchema` and produces the form, fetch,
 * and save behavior automatically.
 *
 * Adding a new live-config doc going forward = one entry in this file. No new
 * components required (unless a brand-new field type is needed).
 */

import {
  DEFAULT_WHATS_NEW_CONFIG,
  WHATS_NEW_BANNER_STORAGE_PREFIX,
} from '../../services/whatsNewConstants';

/** Dynamic option source for `string-array` (and any future option-driven field). */
export type ValueSource =
  | { kind: 'static'; options: string[] }
  /** Fetches the public CurrencyFreaks /currency-symbols list (no API key). */
  | { kind: 'currencyfreaks' };

/**
 * Where a panel's field metadata comes from at runtime, for the schemas whose
 * defaults are owned by the backend rather than by this file. Resolved to a
 * `SchemaOverlay` by `resolveSchemaOverlay`.
 */
export type DefaultsSource = { kind: 'aiScoringPenalties'; slot: string };

/**
 * Runtime-resolved metadata for one field, merged over its static definition
 * before render. Only carries what a backend can legitimately own — the value
 * behind an empty input, the permitted range, and whether the field applies at
 * all — never the field's identity or type.
 */
export interface FieldOverlay {
  /** Field does not apply to this doc; not rendered, validated, or saved. */
  hidden?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  defaultValue?: boolean | number;
  /** Appended to the static `help`. */
  helpSuffix?: string;
}

export type SchemaOverlay =
  | { status: 'ready'; fields: Record<string, FieldOverlay> }
  | { status: 'unavailable'; reason: string };

/** Shared metadata on every field. */
type FieldBase = {
  label: string;
  help?: string;
  /** Per-field read-only override — for mixed-mode panels (e.g. `watches`). */
  readonly?: boolean;
  /**
   * This field cannot be described without its overlay (its placeholder, range
   * or applicability is backend-owned), so it is HIDDEN rather than rendered
   * bare when the overlay fails to load. Showing an unsourced input would
   * invite an edit we can't range-check against a default we can't display.
   */
  requiresOverlay?: boolean;
};

export type BooleanField = FieldBase & {
  type: 'boolean';
  defaultValue?: boolean;
};

export type NumberField = FieldBase & {
  type: 'number';
  min?: number;
  max?: number;
  defaultValue?: number;
  /**
   * Empty is a legal value meaning "no value stored". Saving an emptied
   * optional field DELETES the key rather than writing a number, which is what
   * lets an override fall back to a backend default that can still move.
   */
  optional?: boolean;
  /** Shown when the input is empty — the conventional home for "default: x". */
  placeholder?: string;
  /**
   * Input granularity. Defaults to whole numbers, which is right for the counts
   * and day/month thresholds most of these fields hold, but makes the spinner
   * unusable on a fractional field.
   */
  step?: number | 'any';
};

export type StringField = FieldBase & {
  type: 'string';
  placeholder?: string;
  defaultValue?: string;
  /** Blank (after trim) blocks Save. */
  required?: boolean;
  /** Longest allowed length, after trim. */
  maxLength?: number;
  /** Must start with http:// or https:// when non-blank. */
  url?: boolean;
};

/**
 * An uploaded (or pasted) image: a ready-to-render public URL, plus the Storage
 * path of the bytes when this panel uploaded them.
 *
 * Stored as two keys on the node: the URL under the field's own key, and the
 * path under `storagePathKey` (a sibling, never a schema field of its own). The
 * pair is edited and saved together so they can never disagree.
 *
 * Uploads go to `storagePrefix`, which needs a matching watchlock
 * `storage.rules` block (admin write, public read, same size/type caps).
 */
export type ImageField = FieldBase & {
  type: 'image';
  storagePrefix: string;
  storagePathKey: string;
  /** Wider images are downscaled before upload. */
  maxWidth: number;
  maxBytes: number;
  /** The aspect ratio the app renders this image at, for the preview crop. */
  previewAspect: { width: number; height: number };
  /** Advisory: below this the preview warns the image may look soft. */
  recommendedMinWidth?: number;
};

/** A calendar date, stored as `YYYY-MM-DD`. Always optional: blank stores nothing. */
export type DateField = FieldBase & {
  type: 'date';
};

/**
 * An in-app deep link or an external http(s) URL, stored as the link string —
 * edited with the same picker as banners and push notifications, and validated
 * by the same parser, so only a link the app can route can be saved.
 */
export type DeepLinkField = FieldBase & {
  type: 'deep-link';
};

export type SelectField = FieldBase & {
  type: 'select';
  options: { value: string; label: string }[];
  defaultValue?: string;
};

/** Masked secret. Renders masked + read-only with a "Reveal & Edit" toggle. */
export type PasswordField = FieldBase & {
  type: 'password';
  placeholder?: string;
  defaultValue?: string;
};

/** Chip list of strings, with optional dynamic/static option source. */
export type StringArrayField = FieldBase & {
  type: 'string-array';
  defaultValue?: string[];
  valueSource?: ValueSource;
};

/** Pure display — renders the stored value as text, never editable. */
export type ReadonlyField = FieldBase & {
  type: 'readonly';
};

/**
 * A daily allowance: a non-negative whole number, or the literal `'unlimited'`.
 *
 * Its own type rather than a number with a sentinel because the two halves have
 * different validation and different inputs — see `CapValueInput` for why
 * `'unlimited'` is a checkbox and never typed.
 */
export type CapField = FieldBase & {
  type: 'cap';
  defaultValue?: number | 'unlimited';
};

/** A nested object (one level), rendered as a labelled sub-card. */
export type ObjectField = FieldBase & {
  type: 'object';
  fields: Record<
    string,
    | NumberField
    | StringField
    | BooleanField
    | StringArrayField
    | ReadonlyField
    | CapField
    | SelectField
    | ImageField
    | DateField
    | DeepLinkField
  >;
  /**
   * Cross-field rule over the object AS IT WOULD BE STORED: plain strings, an
   * image as its URL key plus its path key, a deep link as its link string, and
   * (with `omitBlank`) blank keys absent. Returns the message that blocks Save,
   * or null.
   */
  validate?: (stored: Record<string, unknown>) => string | null;
  /**
   * Blank optional sub-values (empty strings and dates, a cleared image) are
   * left out of the stored object rather than written as ''. The object is
   * written whole, so a cleared key disappears from the node.
   */
  omitBlank?: boolean;
};

/** An array of uniform objects (a repeater), e.g. delivery companies. */
export type ObjectArrayField = FieldBase & {
  type: 'object-array';
  itemFields: Record<string, StringField | NumberField>;
};

export type FieldType =
  | BooleanField
  | NumberField
  | StringField
  | SelectField
  | PasswordField
  | StringArrayField
  | ReadonlyField
  | CapField
  | ImageField
  | DateField
  | DeepLinkField
  | ObjectField
  | ObjectArrayField;

export type ConfigSchema = {
  /** Doc path. Firestore: 'collection/docId'. RTDB: '/path/to/node' (leading slash optional). */
  path: string;
  database: 'firestore' | 'rtdb';
  title: string;
  description?: string;
  /** Whole-panel read-only (hides Save, disables every field). */
  readonly?: boolean;
  /** Sub-group within Live Settings; rendered as a collapsible sub-section. */
  group?: string;
  /** Fetches runtime field metadata (defaults, ranges, applicability) on load. */
  defaultsSource?: DefaultsSource;
  /**
   * Amber callout rendered immediately above Save — for a node whose blast
   * radius is not obvious from its field labels. Read at the moment of
   * committing rather than in the header, where it would be scrolled past.
   */
  warning?: string;
  /**
   * Show the raw stored node, verbatim, in a collapsed block.
   *
   * Worth it where "absent" and "written, and happens to equal the default"
   * are different facts an operator needs before touching the node — every
   * input reads the same either way, and only the raw value distinguishes them.
   */
  showLiveValue?: boolean;
  /**
   * First-time setup for a node the app reads with bundled defaults. When the
   * node is absent the panel offers a button that calls this; it must write only
   * if the node is still absent, and resolve true when it created it.
   */
  seed?: { label: string; run: () => Promise<boolean> };
  fields: Record<string, FieldType>;
};

/** Default supported currency codes — fallback for the CurrencyFreaks picker and
 *  the `currency.supportedSymbols` default (mirrors the RN app's constant). */
export const DEFAULT_SUPPORTED_CURRENCIES = [
  'CAD', 'USD', 'EUR', 'GBP', 'CHF', 'AUD', 'NZD', 'JPY',
  'SGD', 'HKD', 'AED', 'SEK', 'KRW', 'CNY',
];

/** Sub-group display order within Live Settings. */
export const GROUP_ORDER = [
  'Feature Toggles',
  'Thresholds & Limits',
  'Currency',
  'Search & Content',
  'Shipping',
  'External Links',
  'AI Scoring',
  'AI Identify',
  'AI Limits',
  'Explore',
];

// ── Feature toggles ──────────────────────────────────────────────────────────

export const watchChartsSchema: ConfigSchema = {
  path: 'configuration/watchCharts',
  database: 'rtdb',
  title: 'WatchCharts Pricing',
  group: 'Feature Toggles',
  description:
    'Master switch for the WatchCharts pricing feature in the RN app. When enabled, horologist users see live market pricing on watch detail screens.',
  fields: {
    enabled: {
      type: 'boolean',
      label: 'Enable WatchCharts pricing',
      help: 'When on, the pricing tab + current value surfacing appear for horologist users. Off = feature hidden across the RN app.',
      defaultValue: false,
    },
    enable5y: {
      type: 'boolean',
      label: 'Enable 5-year history',
      help: '5-year history costs 8 credits per fetch vs 3 for 1-year. Only enable on the paid tier. When off, the 5Y chart toggle is hidden.',
      defaultValue: false,
    },
  },
};

export const articlesSchema: ConfigSchema = {
  path: 'configuration/articles',
  database: 'rtdb',
  title: 'Articles',
  group: 'Feature Toggles',
  description: 'Controls the Articles tab visibility and gating.',
  fields: {
    enabled: {
      type: 'boolean',
      label: 'Enable Articles tab',
      help: 'Master switch. Off hides the Articles tab entirely.',
      defaultValue: true,
    },
    minArticlesBeforeDisplay: {
      type: 'number',
      label: 'Min published articles before display',
      help: 'The tab only appears once at least this many articles are published. Default 3.',
      min: 0,
      defaultValue: 3,
    },
  },
};

export const communityContributionSchema: ConfigSchema = {
  path: 'configuration/communityContribution',
  database: 'rtdb',
  title: 'Community Contribution',
  group: 'Feature Toggles',
  description:
    'Master switch for user-submitted reference contributions (the feature counterpart to User Submission Settings).',
  fields: {
    enabled: {
      type: 'boolean',
      label: 'Enable community contributions',
      help: 'When off, the app blocks all reference submissions and updates to the community database.',
      defaultValue: true,
    },
  },
};

export const enrichmentSchema: ConfigSchema = {
  path: 'configuration/enrichment',
  database: 'rtdb',
  title: 'AI Enrichment',
  group: 'Feature Toggles',
  description:
    'Master switch for AI-powered watch insights (service tips, locations, brand history).',
  fields: {
    enabled: {
      type: 'boolean',
      label: 'Enable AI insights',
      help: 'When off, the enrichment panel is hidden on the watch detail screen.',
      defaultValue: true,
    },
  },
};

export const productLinksSchema: ConfigSchema = {
  path: 'configuration/productLinks',
  database: 'rtdb',
  title: 'Reference Product Links',
  group: 'Feature Toggles',
  description:
    'Controls whether the "View product page" link appears on reference detail screens in the explore section.',
  fields: {
    enabled: {
      type: 'boolean',
      label: 'Enable product links',
      help: 'Master switch. When off, no product links show on any reference, regardless of brand-level settings.',
      defaultValue: true,
    },
  },
};

// ── Thresholds & limits ──────────────────────────────────────────────────────

export const submissionSettingsSchema: ConfigSchema = {
  path: 'configuration/submission',
  database: 'rtdb',
  title: 'User Submission Settings',
  group: 'Thresholds & Limits',
  description: 'Controls the gate for user-submitted reference contributions.',
  fields: {
    minTenureDays: {
      type: 'number',
      label: 'Minimum account age (days)',
      help: 'Users must have an account at least this old (in days) before they can submit references. Default 90.',
      min: 0,
      defaultValue: 90,
    },
  },
};

export const valueHistorySchema: ConfigSchema = {
  path: 'configuration/valueHistory',
  database: 'rtdb',
  title: 'Value History',
  description:
    'Cap on the number of valuation entries per watch. Older entries are time-bucket compacted when the cap is exceeded.',
  fields: {
    maxEntries: {
      type: 'number',
      label: 'Max entries per watch',
      help: 'Default 200. Affects when oldest entries are combined for room.',
      defaultValue: 200,
    },
  },
  group: 'Thresholds & Limits',
};

export const pricingSchema: ConfigSchema = {
  path: 'configuration/pricing',
  database: 'rtdb',
  title: 'Pricing Display',
  group: 'Thresholds & Limits',
  description: 'Controls how pricing data is presented in the RN app.',
  fields: {
    stalenessDays: {
      type: 'number',
      label: 'Pricing staleness threshold (days)',
      help: 'Show a warning icon next to pricing when the data is older than this many days. Default 90.',
      min: 1,
      defaultValue: 90,
    },
  },
};

export const depthRatingSchema: ConfigSchema = {
  path: 'configuration/depthRating',
  database: 'rtdb',
  title: 'Depth Rating / Service Warning',
  group: 'Thresholds & Limits',
  description:
    'Controls the vintage water-resistance "recent service" window used for the depth-rating warning in the RN app.',
  fields: {
    serviceWarningMonths: {
      type: 'number',
      label: 'Recent-service window (months)',
      help: 'A service within this many months counts as "recent"; older than this triggers the vintage water-resistance warning. Default 24.',
      min: 1,
      defaultValue: 24,
    },
  },
};

export const authSchema: ConfigSchema = {
  path: 'configuration/auth',
  database: 'rtdb',
  title: 'Authentication',
  group: 'Thresholds & Limits',
  description: 'Password and sign-in rules for the RN app.',
  fields: {
    minPasswordLength: {
      type: 'number',
      label: 'Minimum password length',
      help: 'Enforced on signup and password reset. Default 6.',
      min: 4,
      max: 64,
      defaultValue: 6,
    },
  },
};

export const shakeReportSchema: ConfigSchema = {
  path: 'configuration/shakeReport',
  database: 'rtdb',
  title: 'Shake-to-Report',
  group: 'Thresholds & Limits',
  description: 'Rate limiting for shake-to-report bug submissions.',
  fields: {
    rateLimitEnabled: {
      type: 'boolean',
      label: 'Enable rate limiting',
      help: 'Turn on before go-live.',
      defaultValue: false,
    },
    maxReportsPerHour: {
      type: 'number',
      label: 'Max reports per hour',
      help: 'Per rolling hour, when rate limiting is on. Default 3.',
      min: 1,
      defaultValue: 3,
    },
  },
};

// ── Currency ─────────────────────────────────────────────────────────────────

export const currencySchema: ConfigSchema = {
  path: 'configuration/currency',
  database: 'rtdb',
  title: 'Currency',
  group: 'Currency',
  description:
    'Currency list, base, cache TTL, and the CurrencyFreaks API key used for exchange rates.',
  fields: {
    supportedSymbols: {
      type: 'string-array',
      label: 'Supported currencies',
      help: 'Currency codes offered throughout the app. Picker is sourced live from CurrencyFreaks.',
      valueSource: { kind: 'currencyfreaks' },
      defaultValue: [...DEFAULT_SUPPORTED_CURRENCIES],
    },
    baseCurrency: {
      type: 'string',
      label: 'Base currency',
      help: 'Conversion base. Read-only — high blast radius. Default CAD.',
      readonly: true,
      defaultValue: 'CAD',
    },
    cacheTimeoutMs: {
      type: 'number',
      label: 'Rate cache TTL (ms)',
      help: 'How long fetched exchange rates are cached. Default 86400000 (24h).',
      min: 60000,
      defaultValue: 86400000,
    },
    apiKey: {
      type: 'password',
      label: 'CurrencyFreaks API key',
      help: 'Masked. "Reveal & Edit" to change. Overrides the key bundled in the RN app, live.',
    },
  },
};

// ── Search & content ─────────────────────────────────────────────────────────

export const generalSchema: ConfigSchema = {
  path: 'configuration/general',
  database: 'rtdb',
  title: 'General',
  group: 'Search & Content',
  description: 'Curated-search behavior and misc app settings.',
  fields: {
    showCuratedBrandImagesInSearch: {
      type: 'boolean',
      label: 'Show curated brand images in search',
      help: 'When on, curated brand images are fetched and shown in search results.',
      defaultValue: true,
    },
    copyImagesFromCuratedBrands: {
      type: 'string-array',
      label: 'Copy images from curated brands',
      help: "Brand names (case-insensitive) whose curated images are auto-copied onto a watch when it's added — bypasses the per-add opt-in. Empty = copy only when the user opts in.",
      defaultValue: [],
    },
  },
};

// ── Shipping ─────────────────────────────────────────────────────────────────

export const shippingSchema: ConfigSchema = {
  path: 'configuration/shipping',
  database: 'rtdb',
  title: 'Shipping / Delivery Companies',
  group: 'Shipping',
  description:
    'Carriers offered for sold-watch tracking. Use {tracking-number} as the placeholder in the URL template.',
  fields: {
    deliveryCompanies: {
      type: 'object-array',
      label: 'Delivery companies',
      help: 'Each row: a display name and a tracking-URL template containing {tracking-number}.',
      itemFields: {
        name: { type: 'string', label: 'Name', placeholder: 'Canada Post' },
        trackingUrlTemplate: {
          type: 'string',
          label: 'Tracking URL template',
          placeholder: 'https://…/{tracking-number}',
        },
      },
    },
  },
};

// ── External links ───────────────────────────────────────────────────────────

export const salePostSchema: ConfigSchema = {
  path: 'configuration/salePost',
  database: 'rtdb',
  title: 'Sale Posts',
  group: 'External Links',
  description: 'Generated sale-post footer and store links.',
  fields: {
    footerEnabled: {
      type: 'boolean',
      label: 'Include "Generated by Watch Keeper" footer',
      defaultValue: true,
    },
    appStoreUrl: {
      type: 'string',
      label: 'iOS App Store URL',
      help: 'Read-only — changing store links is rare and high-risk.',
      readonly: true,
    },
    playStoreUrl: {
      type: 'string',
      label: 'Google Play URL',
      help: 'Read-only — changing store links is rare and high-risk.',
      readonly: true,
    },
  },
};

// ── Watches ──────────────────────────────────────────────────────────────────

export const watchesSchema: ConfigSchema = {
  path: 'configuration/watches',
  database: 'rtdb',
  title: 'Watches',
  group: 'Thresholds & Limits',
  description: 'Vintage-watch threshold used by the water-resistance warning.',
  fields: {
    vintageAgeYears: {
      type: 'number',
      label: 'Vintage age (years)',
      help: 'A watch older than this triggers the vintage water-resistance warning (pairs with Depth Rating). Default 30.',
      min: 1,
      defaultValue: 30,
    },
  },
};

// ── AI scoring penalties ─────────────────────────────────────────────────────

/**
 * De-boosts applied by the two AI match scorers when a candidate CONTRADICTS a
 * confidently-read attribute, or has no value for it at all. One RTDB node per
 * slot under `configuration/aiScoringPenalties`, one panel each.
 *
 * The slot list, the shipped magnitudes behind every empty input, the permitted
 * range and which slots honour a `near` tier are all owned by watchlock's
 * `functions/src/scoringPenalties.ts` and served by the
 * `getAiScoringPenaltyDefaults` callable — deliberately NOT restated here. Only
 * the five slot keys are local, and a drift in that list is self-announcing: a
 * slot watchlock drops stops resolving an overlay and hides its own magnitude
 * fields, and a slot watchlock adds simply has no panel until one is added
 * below. Nothing silently displays a wrong number.
 */
const PENALTY_SLOTS: { slot: string; title: string; description: string }[] = [
  {
    slot: 'caseShape',
    title: 'Case Shape',
    description:
      'The most reliable slot — a gross silhouette judgement that survives bad lighting and odd angles. The only slot with a `near` tier: an adjacent shape (a cushion read as square) is charged as an honest misread rather than a contradiction.',
  },
  {
    slot: 'caseSizeMm',
    title: 'Case Size',
    description:
      'Graded numerically rather than by family: within tolerance is a match, and beyond it the charge ramps with the size error. The catalogue’s 0 is an "unknown" sentinel, so it counts as absent, never as wrong by 40mm.',
  },
  {
    slot: 'movementType',
    title: 'Movement Type',
    description:
      'Only ever charged across the mechanical/electronic divide — automatic-vs-manual is never penalised, since no still photograph can settle it.',
  },
  {
    slot: 'caseMaterial',
    title: 'Case Material',
    description:
      'Grouped by appearance rather than composition: white gold, platinum, titanium and steel are one silver-grey mass to a camera, so only a warm/cool/technical crossing is charged.',
  },
  {
    slot: 'dialColor',
    title: 'Dial Color',
    description:
      'Family-aware (red = burgundy = oxblood), so only a genuine cross-family disagreement reaches the penalty.',
  },
];

/**
 * One panel per slot: its own RTDB node, so Save writes exactly one slot and a
 * mistake in one can never take another with it.
 *
 * `nearPenalty` is declared on every slot and hidden by the overlay wherever the
 * slot's classifier cannot return `near`. Declaring it locally only where it
 * applies today would restate a backend rule that is free to change.
 */
function penaltySchema({
  slot,
  title,
  description,
}: {
  slot: string;
  title: string;
  description: string;
}): ConfigSchema {
  const magnitude = (label: string, help: string): NumberField => ({
    type: 'number',
    label,
    help,
    optional: true,
    requiresOverlay: true,
    // Penalties are hundredths; whole-number stepping would jump straight past
    // the permitted range on a single arrow press.
    step: 0.01,
  });
  return {
    path: `configuration/aiScoringPenalties/${slot}`,
    database: 'rtdb',
    title,
    group: 'AI Scoring',
    description,
    defaultsSource: { kind: 'aiScoringPenalties', slot },
    fields: {
      mismatch: {
        type: 'boolean',
        label: 'Penalise contradictions',
        help: 'Charges a candidate that holds a DIFFERENT value for this slot. Safe at any catalogue coverage — it only ever fires on data we actually hold.',
      },
      null: {
        type: 'boolean',
        label: 'Penalise missing values',
        help: 'Charges a candidate with no value for this slot. Needs coverage: below it, nearly every candidate takes the same hit and scores sink without anything being reordered.',
      },
      mismatchPenalty: magnitude(
        'Contradiction penalty',
        'Overrides the shipped magnitude charged when the candidate contradicts the extraction.',
      ),
      nearPenalty: magnitude(
        'Near-miss penalty',
        'Overrides the shipped magnitude charged when the candidate is in the same family as the extraction — an honest misread rather than a contradiction.',
      ),
      nullPenalty: magnitude(
        'Missing-value penalty',
        'Overrides the shipped magnitude charged when the candidate has no value for this slot.',
      ),
    },
  };
}

export const aiScoringPenaltySchemas: ConfigSchema[] = PENALTY_SLOTS.map(penaltySchema);

// ── AI identify ──────────────────────────────────────────────────────────────

/**
 * When the reverse image pass runs during an AI identification.
 *
 * This is a cost dial, not a feature switch. The pass costs one provider call
 * per identification and the free SerpApi tier is 250 calls a month, so
 * `weakOnly` exists to throttle to the cases that need it most without turning
 * verification off entirely.
 *
 * The node also carries `maxMatches`, `baseConfidence` and `corroborationBonus`.
 * They are deliberately NOT declared here: `writeConfig` patches only the keys a
 * panel actually renders, via `update()` on the parent node, so the three
 * survive a save untouched. They stay on the console until there is a reason to
 * expose them — at which point they need their permitted ranges sourced from the
 * backend rather than restated here, because an out-of-range value is silently
 * ignored by `getReverseImageConfig` rather than rejected.
 */
export const reverseImageSearchSchema: ConfigSchema = {
  path: 'configuration/aiIdentify/reverseImageSearch',
  database: 'rtdb',
  title: 'Reverse Image Search',
  group: 'AI Identify',
  description:
    'Whether an identification also runs the photo through reverse image search to verify the catalogue’s answer against the open web.',
  fields: {
    mode: {
      type: 'select',
      label: 'When to run',
      help: 'Always = one provider call per identification; catches confidently-wrong matches, which the weak-only gate never fired on. Weak matches only = the legacy behaviour, fires just when the text web-search gate does. Off = never runs, no spend.',
      options: [
        { value: 'always', label: 'Always — every identification' },
        { value: 'weakOnly', label: 'Weak matches only — when the text search gate fires' },
        { value: 'off', label: 'Off — never run' },
      ],
      defaultValue: 'always',
    },
  },
};

// ── AI limits ────────────────────────────────────────────────────────────────

/**
 * The daily AI rate limit — which limiter is live, each tier's allowance, and
 * the free-tier feature gates.
 *
 * `mode` is the switch between two spend models: the monthly credit ledger that
 * is running today, and the per-day cap that replaces it. It ships as 'credits'
 * and only an operator write turns the cap on, which is what this panel exists
 * to be. Every value watchlock fails to recognise resolves back to 'credits',
 * so the risk this panel carries is not a typo — it is a deliberate flip made
 * before the client build that explains a cap to a user is in the field.
 *
 * The caps are declared with the magnitudes watchlock ships, and saving writes
 * all four. watchlock tolerates a partial `caps` node (an absent tier keeps its
 * shipped default), so pinning all four does give up the ability to inherit a
 * default that later moves. That is the intended trade: an operator turning on
 * a limiter should see and own every number it will enforce, not inherit two of
 * them invisibly. The shipped values are pinned by `configSchema.test.ts`, so a
 * change on the watchlock side surfaces as a failing test here.
 */
export const aiRateLimitSchema: ConfigSchema = {
  path: 'configuration/aiRateLimit',
  database: 'rtdb',
  title: 'AI Rate Limit',
  group: 'AI Limits',
  description:
    'Which AI spend limiter is live, the per-tier daily allowance the daily limiter enforces, and the free-tier feature gates.',
  showLiveValue: true,
  warning:
    'Flipping mode to "daily" starts enforcing per-tier daily caps immediately. Ensure the client build carrying the daily-cap UX (PR 2, deployed 2026-08-15) is in the field before flipping. Each free-tier gate withholds a feature free users have today — turning one on is a visible removal, not a no-op.',
  fields: {
    mode: {
      type: 'select',
      label: 'Limiter mode',
      help: 'Credits = the monthly credit ledger running today. Daily = the per-tier daily cap below. Only one is ever live. Anything watchlock cannot read resolves to credits.',
      options: [
        { value: 'credits', label: 'Credits — monthly credit ledger (current)' },
        { value: 'daily', label: 'Daily — per-tier daily cap' },
      ],
      defaultValue: 'credits',
    },
    caps: {
      type: 'object',
      label: 'Daily caps',
      help: 'Calls per UTC day, per tier. Only enforced while mode is "daily". A cap of 0 blocks the tier outright; the counter rolls over at UTC midnight.',
      fields: {
        free: {
          type: 'cap',
          label: 'Free',
          help: 'Ships at 0 — under the daily limiter a free user gets nothing until an operator writes a number.',
          defaultValue: 0,
        },
        collector: { type: 'cap', label: 'Collector', defaultValue: 20 },
        curator: { type: 'cap', label: 'Curator', defaultValue: 50 },
        horologist: {
          type: 'cap',
          label: 'Horologist',
          defaultValue: 'unlimited',
        },
      },
    },
    freeGates: {
      type: 'object',
      label: 'Free-tier gates',
      help: 'Kill switches, independent of the limiter mode: off = no gate, which is today’s behaviour. Both ship off.',
      fields: {
        webSearch: {
          type: 'boolean',
          label: 'Gate free-tier out of web-search',
          help: 'On = free users no longer get the text web-search pass during an identification.',
          defaultValue: false,
        },
        reverseImageSearch: {
          type: 'boolean',
          label: 'Gate free-tier out of reverse-image-search',
          help: 'On = free users no longer get the reverse-image verification pass during an identification.',
          defaultValue: false,
        },
      },
    },
  },
};

/**
 * How hard the server-side AI consent gate bites.
 *
 * Shipped by the consent PR at 'log-only', which inspects and reports and
 * rejects nobody. It lives beside the rate limit because the rollout flips both
 * in one operator session, and a second RTDB console visit is where a
 * half-finished flip comes from.
 *
 * The population that matters is `missing` — users on a build that never asked
 * for consent. Enforcing before that share drains rejects them from Identify,
 * Smart Search and Reference Lookup at once, and it reads to them as a broken
 * feature rather than as a choice they made.
 */
export const aiConsentSchema: ConfigSchema = {
  path: 'configuration/aiConsent',
  database: 'rtdb',
  title: 'AI Consent Enforcement',
  group: 'AI Limits',
  description:
    'How hard the server-side AI consent gate bites. Ships at log-only, which reports would-have-rejected volume without turning anyone away.',
  showLiveValue: true,
  warning:
    '"Enforce" rejects every user with no stored consent, including anyone on a build that never asked them. Watch the would-have-rejected volume on log-only until the `missing` share is ~zero before enforcing.',
  fields: {
    serverEnforcement: {
      type: 'select',
      label: 'Server enforcement',
      help: 'Off = no server check. Log-only = check and report, reject nobody. Enforce = reject declined and missing consent.',
      options: [
        { value: 'off', label: 'Off — no server-side check' },
        { value: 'log-only', label: 'Log only — report, reject nobody (current)' },
        { value: 'enforce', label: 'Enforce — reject declined and missing' },
      ],
      defaultValue: 'log-only',
    },
  },
};

// ── Explore ──────────────────────────────────────────────────────────────────

/**
 * What's New (Confluence WK 2287861761, §3.1). The RN app reads this node live;
 * the Collections editor reads `title` to lock the What's New card's name.
 * Every field is optional on the node — RN ships the same defaults — so the
 * seed button only makes the node explicit.
 */
export const whatsNewSchema: ConfigSchema = {
  path: 'configuration/whatsNew',
  database: 'rtdb',
  title: "What's New",
  group: 'Explore',
  description:
    "Newly added brands and references: the What's New card in Explore Collections, the full " +
    '/whats-new page and the brand page\'s "Recently added" strip. "New" means created within the ' +
    'window — re-imports and enrichment never count. Turn it on with visibility "Admins only" to ' +
    'preview on your own device before switching to "Everyone". The Advanced Search "Added" ' +
    'filter is not affected by any setting here.',
  showLiveValue: true,
  seed: {
    label: 'Create with defaults (off, admins only)',
    run: async () => (await import('../../services/whatsNewConfig')).seedWhatsNewConfigIfAbsent(),
  },
  fields: {
    enabled: {
      type: 'boolean',
      label: "Enable What's New",
      defaultValue: DEFAULT_WHATS_NEW_CONFIG.enabled,
      help:
        'Master switch. Off hides the Collections card and the brand-page strip, and the page shows ' +
        '"paused".',
    },
    visibility: {
      type: 'select',
      label: 'Who sees it',
      options: [
        { value: 'admins', label: 'Admins only — live preview' },
        { value: 'everyone', label: 'Everyone' },
      ],
      defaultValue: DEFAULT_WHATS_NEW_CONFIG.visibility,
      help: 'Applies only while enabled. "Admins only" shows every surface to admin accounts and nobody else.',
    },
    title: {
      type: 'string',
      label: 'Title',
      defaultValue: DEFAULT_WHATS_NEW_CONFIG.title,
      placeholder: DEFAULT_WHATS_NEW_CONFIG.title,
      required: true,
      maxLength: 40,
      help:
        'Shared by the Collections card and the page header. The Collections editor locks the card ' +
        'name to this — re-save the card after changing it.',
    },
    windowDays: {
      type: 'number',
      label: 'Window (days)',
      min: 1,
      max: 90,
      defaultValue: DEFAULT_WHATS_NEW_CONFIG.windowDays,
      help:
        "How far back \"new\" reaches; the only window (deep links can't override it). Raising it " +
        'past about a week adds far fewer brands than the raw counts suggest: a new brand also needs ' +
        'a rich description, and older imports mostly have none (Oct 2026: 31 of the 117 brands ' +
        'created in 30 days have one, vs 30 of 30 in 7 days).',
    },
    enrichedRefsOnly: {
      type: 'boolean',
      label: 'AI-enriched references only',
      defaultValue: DEFAULT_WHATS_NEW_CONFIG.enrichedRefsOnly,
      help:
        'Counts and lists only references with an AI-written description. A new brand with none ' +
        'drops out entirely (Oct 2026, 7 days: 29 brands · 1,908 refs → 26 brands · 711 refs).',
    },
    requireRefImage: {
      type: 'boolean',
      label: 'Require a reference image',
      defaultValue: DEFAULT_WHATS_NEW_CONFIG.requireRefImage,
      help: 'Leaves out references without an image — image-less cards look broken in a visual feed.',
    },
    autoBanner: {
      type: 'select',
      label: 'Automatic banner',
      options: [
        { value: 'hero+fallback', label: 'Brand hero, else the generic banner pool' },
        { value: 'hero', label: 'Brand hero only' },
        { value: 'off', label: 'Off — plain header unless a banner is configured below' },
      ],
      defaultValue: DEFAULT_WHATS_NEW_CONFIG.autoBanner,
      help:
        'Used when no configured banner is showing. "Brand hero" picks the eligible brand with a hero ' +
        'image and the most new references; the pool is Brand Banner Fallbacks. Few brands have a ' +
        'hero today, so the pool is the usual result.',
    },
    banner: {
      type: 'object',
      label: 'Configured banner',
      help:
        'The page header image (full page only, about 2.4:1). When enabled, with an image and within ' +
        'its dates, it wins over the automatic banner.',
      omitBlank: true,
      validate: validateWhatsNewBanner,
      fields: {
        enabled: { type: 'boolean', label: 'Show this banner', defaultValue: false },
        imageUrl: {
          type: 'image',
          label: 'Image',
          storagePrefix: WHATS_NEW_BANNER_STORAGE_PREFIX,
          storagePathKey: 'storagePath',
          maxWidth: 2400,
          maxBytes: 10 * 1024 * 1024,
          previewAspect: { width: 12, height: 5 },
          recommendedMinWidth: 1170,
        },
        caption: { type: 'string', label: 'Caption', maxLength: 80, placeholder: 'Fresh from the bench' },
        subcaption: { type: 'string', label: 'Subcaption (optional)', maxLength: 120 },
        ctaLabel: { type: 'string', label: 'Button text (optional)', maxLength: 24 },
        ctaLink: { type: 'deep-link', label: 'Button link (optional)' },
        startsAt: { type: 'date', label: 'Show from (optional)' },
        endsAt: { type: 'date', label: 'Show until (optional)' },
        creditUrl: {
          type: 'string',
          label: 'Photo credit link (optional)',
          url: true,
          placeholder: 'https://…',
        },
      },
    },
  },
};

/** Cross-field rules for the configured banner. Exported for tests. */
export function validateWhatsNewBanner(v: Record<string, unknown>): string | null {
  const str = (x: unknown) => (typeof x === 'string' ? x.trim() : '');
  if (v.enabled === true && !str(v.imageUrl)) return 'An enabled banner needs an image';
  if (v.enabled === true && !str(v.caption)) return 'An enabled banner needs a caption';
  if (!!str(v.ctaLabel) !== !!str(v.ctaLink)) return 'Set both the button text and its link, or neither';
  const from = str(v.startsAt);
  const until = str(v.endsAt);
  if (from && until && until < from) return '"Show until" is before "Show from"';
  return null;
}

/** Ordered list rendered by AdminConfigPage. Grouped by `group` in display order. */
export const CONFIG_SCHEMAS: ConfigSchema[] = [
  // Feature Toggles
  watchChartsSchema,
  articlesSchema,
  communityContributionSchema,
  enrichmentSchema,
  productLinksSchema,
  // Thresholds & Limits
  submissionSettingsSchema,
  valueHistorySchema,
  pricingSchema,
  depthRatingSchema,
  authSchema,
  shakeReportSchema,
  watchesSchema,
  // Currency
  currencySchema,
  // Search & Content
  generalSchema,
  // Shipping
  shippingSchema,
  // External Links
  salePostSchema,
  // AI Scoring
  ...aiScoringPenaltySchemas,
  // AI Identify
  reverseImageSearchSchema,
  // AI Limits
  aiRateLimitSchema,
  aiConsentSchema,
  // Explore
  whatsNewSchema,
];
